<!--
  Nuova sede / modifica sede di un partner.

  Condiviso da dettaglio partner e dettaglio richiesta di prenotazione: stessi
  campi e stessa geocodifica della creazione partner. Le scritture su
  /headquarter sono riservate a Simba (GLOBAL_ROLES).
  Emette `saved` con la sede salvata (anche l'id, se è nuova).
-->
<template>
  <el-dialog
    v-model="visible"
    append-to-body
    :title="draft.id ? 'Modifica sede' : 'Nuova sede'"
    width="620px"
  >
    <div
      v-loading="loadingHq"
      class="hqd__form"
    >
      <div class="hqd__fld">
        <label>Nome sede</label>
        <el-input
          v-model="draft.name"
          placeholder="Es. Milano Centro"
        />
      </div>
      <!-- ⚠️ Via e CIVICO sono due colonne diverse (`address` e
           `street_number`): il civico dentro la via falsa la geocodifica. -->
      <div class="hqd__grid2">
        <div class="hqd__fld">
          <label>Indirizzo</label>
          <el-input
            v-model="draft.address"
            placeholder="Via/piazza, senza civico"
            @input="onAddressInput"
            @blur="maybeGeocode"
          />
        </div>
        <div class="hqd__fld">
          <label>Civico</label>
          <el-input
            v-model="draft.street_number"
            placeholder="Es. 12/A"
            @input="onAddressInput"
            @blur="maybeGeocode"
          />
        </div>
        <div class="hqd__fld">
          <label>Città</label>
          <el-input
            v-model="draft.city"
            @input="onAddressInput"
            @blur="maybeGeocode"
          />
        </div>
        <div class="hqd__fld">
          <label>CAP</label>
          <el-input
            v-model="draft.cap"
            @input="onAddressInput"
            @blur="maybeGeocode"
          />
        </div>
        <div class="hqd__fld">
          <label>Regione</label>
          <el-select
            v-model="draft.regionId"
            placeholder="Scegli la regione"
            filterable
            style="width: 100%"
            @change="onRegionChange"
          >
            <el-option
              v-for="r in regions"
              :key="r.id"
              :label="r.name"
              :value="r.id"
            />
          </el-select>
        </div>
        <div class="hqd__fld">
          <label>Provincia</label>
          <el-select
            v-model="draft.provinceId"
            :placeholder="draft.province && !draft.provinceId
              ? draft.province : 'Scegli la provincia'"
            filterable
            :disabled="!draft.regionId"
            style="width: 100%"
            @change="onProvinceChange"
          >
            <el-option
              v-for="p in provinces"
              :key="p.id"
              :label="`${p.name} (${p.sigla})`"
              :value="p.id"
            />
          </el-select>
        </div>
      </div>
      <div class="hqd__fld">
        <label>Coordinate</label>
        <div class="hqd__geo">
          <el-button
            size="small"
            :loading="draft.geoState === 'loading'"
            @click="geocode(true)"
          >
            Verifica indirizzo
          </el-button>
          <span
            v-if="draft.geoState === 'ok'"
            class="hqd__geoMsg is-ok"
          >📍 {{ draft.geoLabel || `${draft.latitude}, ${draft.longitude}` }}</span>
          <span
            v-else-if="draft.geoState === 'fail'"
            class="hqd__geoMsg is-missing"
          >⚠ Indirizzo non trovato: la sede resterebbe senza coordinate</span>
          <span
            v-else-if="draft.geoState === 'incomplete'"
            class="hqd__geoMsg"
          >Compila via, civico, CAP, città e provincia</span>
        </div>
      </div>
      <div class="hqd__fld">
        <label>Abbonamento mensile</label>
        <el-input
          v-model.number="draft.abbonamento"
          type="number"
          step="0.01"
        >
          <template #append>
            €
          </template>
        </el-input>
      </div>
    </div>
    <template #footer>
      <el-button @click="visible = false">
        Annulla
      </el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="!canSave || loadingHq"
        @click="save"
      >
        {{ draft.id ? 'Salva sede' : 'Crea sede' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import partnerAPI from '../services/partnerAPI.js';
import areasAPI from '../services/areasAPI.js';
import prenotazioneAPI from '../services/prenotazioneAPI.js';

const ADDRESS_FIELDS = ['address', 'street_number', 'cap', 'city', 'province'];
const emptyHq = () => ({
  id: null, name: '', address: '', street_number: '', cap: '', city: '',
  province: '', regionId: '', provinceId: '',
  abbonamento: null, latitude: 0, longitude: 0,
  geoState: '', geoLabel: '',
});

// Elenchi di sistema (regioni, zone): letti una volta per sessione, condivisi
// fra tutte le istanze del dialogo.
const regions = ref([]);
let geoListsLoaded = false;
async function loadGeoLists() {
  if (geoListsLoaded) return;
  try {
    regions.value = await areasAPI.getRegionsTree();
    geoListsLoaded = true;
  } catch (e) {
    console.warn('Elenco regioni non disponibile:', e?.message);
  }
}

export default {
  name: 'HeadquarterDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    // partner a cui appartiene la sede: obbligatorio per crearne una nuova
    partnerId: { type: String, default: '' },
    // sede da modificare; null = nuova sede
    headquarter: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved'],

  setup(props, { emit }) {
    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v),
    });
    const draft = ref(emptyHq());
    const loadingHq = ref(false);
    const saving = ref(false);

    const hasCoords = (h) => !!(Number(h?.latitude) && Number(h?.longitude));

    // All'apertura il form riparte da capo (nuova) o dalla sede passata.
    const reset = async () => {
      loadGeoLists();
      const h = props.headquarter;
      if (!h?.id) { draft.value = emptyHq(); return; }
      draft.value = {
        ...emptyHq(),
        ...h,
        geoState: hasCoords(h) ? 'ok' : '',
      };
      loadingHq.value = true;
      try {
        // regione e provincia strutturate non arrivano con il partner
        const full = await partnerAPI.getHeadquarterForEdit(h.id);
        draft.value.regionId = full?.region?.id || '';
        draft.value.provinceId = full?.province_ref?.id || '';
      } catch (e) {
        console.warn('Dettaglio sede non disponibile:', e?.message);
      } finally {
        loadingHq.value = false;
      }
    };
    watch(() => props.modelValue, (open) => { if (open) reset(); }, { immediate: true });

    const provinces = computed(() =>
      regions.value.find((r) => r.id === draft.value.regionId)?.provinces || []
    );

    // Cambiato l'indirizzo, le coordinate vecchie non valgono più.
    const onAddressInput = () => {
      const d = draft.value;
      d.geoState = '';
      d.geoLabel = '';
      d.latitude = 0;
      d.longitude = 0;
    };

    const onRegionChange = () => {
      draft.value.provinceId = '';
      draft.value.province = '';
      onAddressInput();
    };

    const addressComplete = () =>
      ADDRESS_FIELDS.every((f) => String(draft.value[f] || '').trim());

    const geocode = async (forced = false) => {
      const d = draft.value;
      if (!addressComplete()) { d.geoState = 'incomplete'; return; }
      if (d.geoState === 'ok' && !forced) return;
      d.geoState = 'loading';
      try {
        const text = `${d.address} ${d.street_number} ${d.cap}, ${d.city} ${d.province}`;
        const { data } = await prenotazioneAPI.getPoints({ text });
        const first = Array.isArray(data) ? data[0] : null;
        if (first?.point?.latitude) {
          d.latitude = first.point.latitude;
          d.longitude = first.point.longitude;
          d.geoLabel = first.label || text;
          d.geoState = 'ok';
        } else {
          d.geoState = 'fail';
        }
      } catch (e) {
        console.warn('Geocoding non riuscito:', e?.message);
        d.geoState = 'fail';
      }
    };

    const maybeGeocode = () => {
      if (!addressComplete()) {
        if (draft.value.geoState !== 'loading') draft.value.geoState = 'incomplete';
        return;
      }
      geocode();
    };

    // La colonna storica `province` è la SIGLA: si tiene allineata.
    const onProvinceChange = () => {
      const p = provinces.value.find((x) => x.id === draft.value.provinceId);
      draft.value.province = p?.sigla || '';
      onAddressInput();
      maybeGeocode();
    };

    const canSave = computed(() => {
      const d = draft.value;
      return !!String(d.name || '').trim() && !!String(d.address || '').trim()
        && d.geoState !== 'loading' && (!!d.id || !!props.partnerId);
    });

    const save = async () => {
      const d = draft.value;
      saving.value = true;
      const payload = {
        name: d.name.trim(),
        address: d.address.trim(),
        street_number: String(d.street_number || '').trim(),
        cap: String(d.cap || '').trim(),
        city: String(d.city || '').trim(),
        province: d.province,
        abbonamento: Number(d.abbonamento) || 0,
        latitude: d.latitude || 0,
        longitude: d.longitude || 0,
        // `province_ref` perché la colonna stringa `province` occupava il nome.
        // Se non scelte si omettono: il subscriber backend le ricava dalla sigla.
        ...(d.regionId ? { region: { id: d.regionId } } : {}),
        ...(d.provinceId ? { province_ref: { id: d.provinceId } } : {}),
      };
      try {
        let res;
        if (d.id) {
          res = await partnerAPI.updateHeadquarter({ id: d.id, ...payload });
          ElMessage.success('Sede aggiornata');
        } else {
          res = await partnerAPI.addHeadquarter({ ...payload, partner: { id: props.partnerId } });
          ElMessage.success('Sede creata');
        }
        visible.value = false;
        // la sede salvata, con l'id anche quando è nuova
        emit('saved', { ...payload, ...(res?.data || {}), id: res?.data?.id || d.id });
      } catch (e) {
        console.error('Salvataggio sede fallito:', e);
        ElMessage.error(
          e?.response?.status === 403
            ? 'Le sedi possono essere modificate solo da Simba'
            : 'Salvataggio della sede non riuscito'
        );
      } finally {
        saving.value = false;
      }
    };

    return {
      visible, draft, loadingHq, saving, regions, provinces, canSave,
      onAddressInput, onRegionChange, onProvinceChange, geocode, maybeGeocode, save,
    };
  },
};
</script>

<style scoped>
.hqd__form { display: flex; flex-direction: column; gap: 12px; }
.hqd__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 560px) { .hqd__grid2 { grid-template-columns: 1fr; } }
.hqd__fld label {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.hqd__note { margin: 14px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5; }
.hqd__note--attn { margin-top: 6px; color: var(--state-attn); }
.hqd__geo { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.hqd__geoMsg { font-size: 12.5px; color: var(--text-tertiary); }
.hqd__geoMsg.is-ok { color: var(--state-ok); }
.hqd__geoMsg.is-missing { color: var(--state-attn); }
</style>
